import React, { useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Typography,
} from '@mui/material'
import { GridRowsProp } from '@mui/x-data-grid'
import {
  ShareInput,
  ShareReportsPayload,
  useShareReportsMutation,
} from 'gqlTypes'
import ShareDialogSelectReports from './ShareDialog/ShareDialogSelectReports'
import ShareDialogEmails from './ShareDialog/ShareDialogEmails'

interface Props {
  reports: GridRowsProp
  onClose: () => void
}

const ShareDialog: React.FC<Props & DialogProps> = ({
  reports,
  onClose,
  ...dialogProps
}) => {
  const [emails, setEmails] = useState<string | undefined>()
  const [includeBookmarks, setIncludeBookmarks] = useState(false)
  const [results, setResults] = useState<ShareReportsPayload | undefined>()
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([])
  const [selectAllReports, setSelectAllReports] = useState(true)
  const [page, setPage] = useState<'select' | 'email' | 'results'>('select')

  useEffect(() => {
    setSelectedReportIds(reports.map((r) => r.id))
    if (reports.length === 1) {
      setPage('email')
    }
    if (reports.length > 1) {
      setPage('select')
    }
  }, [reports])

  const [shareReportsMutation, { data, loading, error }] =
    useShareReportsMutation()

  const resetForm = () => {
    setSelectedReportIds([])
    setIncludeBookmarks(false)
  }

  const handleClose = () => {
    if (page !== 'results') {
      if (confirm('Are you sure you want to exit before sharing reports?')) {
        resetForm()
        setResults(undefined)
        onClose()
      }
    } else {
      resetForm()
      setResults(undefined)
      onClose()
    }
  }

  const handleToggleReport = (id: string) => {
    const currentIndex = selectedReportIds.indexOf(id)
    const newIds = [...selectedReportIds]
    if (currentIndex === -1) {
      newIds.push(id)
    } else {
      newIds.splice(currentIndex, 1)
    }
    setSelectAllReports(newIds.length === reports.length)
    setSelectedReportIds(newIds)
  }

  const handleToogleAllReports = () => {
    if (selectAllReports) {
      setSelectAllReports(false)
      setSelectedReportIds([])
    } else {
      setSelectAllReports(true)
      setSelectedReportIds(reports.map((r) => r.id))
    }
  }

  const handleChangeEmail = (value: string) => {
    setEmails(value)
  }

  const handleShare = () => {
    if (emails) {
      if (selectedReportIds.length > 0) {
        setPage('results')
        const input: ShareInput = {
          emails: emails,
          includeFavorites: includeBookmarks,
          reportIds: selectedReportIds,
        }
        shareReportsMutation({
          variables: { input: input },
        }).then((rslt) => {
          const shareResult = rslt?.data?.shareReports as ShareReportsPayload
          if (shareResult) {
            if (shareResult.messagePage === 'email') {
              setPage('email')
            }
            setResults(shareResult)
          }
        })
      }
    }
  }

  let title =
    reports.length === 1 ? `Share ${reports[0].title}` : 'Share Reports'
  if (page === 'results' && results && results.success) {
    title = results.message
  }
  return (
    <Dialog fullWidth maxWidth="sm" {...dialogProps} onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {page === 'select' && reports.length > 1 && (
          <ShareDialogSelectReports
            reports={reports}
            selectedReportIds={selectedReportIds}
            selectAllReports={selectAllReports}
            onToggleReport={handleToggleReport}
            onToggleAllReports={handleToogleAllReports}
          />
        )}
        {page === 'email' && (
          <ShareDialogEmails
            results={results}
            reports={reports}
            selectedReportIds={selectedReportIds}
            emails={emails || ''}
            includeBookmarks={includeBookmarks}
            onGoBack={() => setPage('select')}
            onChange={handleChangeEmail}
            onToggleBookmarks={() => setIncludeBookmarks(!includeBookmarks)}
          />
        )}
        {page === 'results' && (
          <>
            {results ? (
              <>
                {results.success ? (
                  <>
                    <Box mb={2}>
                      <Typography>
                        You’ll receive an email from
                        do-not-reply@surveyresults.org once sharing is complete.
                        This can take anywhere from a few seconds to minutes,
                        depending on how many reports you’re sharing.
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography>
                        If you don’t see the email, please check your spam
                        folder.
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography>
                        After sharing is complete, the people you shared reports
                        with will also receive an email from
                        do-not-reply@surveyresults.org with instructions for
                        accessing the reports.
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography>{results.message}</Typography>
                )}
              </>
            ) : (
              <Box display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {page !== 'results' && <Button onClick={handleClose}>Cancel</Button>}
        {page === 'select' && (
          <Button
            disabled={selectedReportIds.length === 0}
            onClick={() => setPage('email')}
          >
            Next
          </Button>
        )}
        {page === 'email' && (
          <Button
            disabled={selectedReportIds.length === 0 || !emails}
            onClick={handleShare}
          >
            Share
          </Button>
        )}
        {page === 'results' && <Button onClick={handleClose}>Done</Button>}
      </DialogActions>
    </Dialog>
  )
}

export default ShareDialog
